// src/routes/App.jsx
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useThemeSync } from "../hooks/useThemeSync";

import MarketingLayout from "../layouts/MarketingLayout";
import AppLayout from "../layouts/AppLayout";
import ConsentBanner from "../analytics/ConsentBanner";
import { initAnalytics, trackPageView } from "../analytics/client";

/* Lazy pages */
const LandingPage = lazy(() => import("../pages/Landing"));
const AboutPage = lazy(() => import("../pages/About"));
const FeaturesPage = lazy(() => import("../pages/Features"));
const JoinPage = lazy(() => import("../pages/Join"));
const LoginPage = lazy(() => import("../pages/Login"));
const SignupPage = lazy(() => import("../pages/SignupPage"));
const LoginPageSkeleton = lazy(() => import("../pages/LoginPage"));
const VerifyEmailPage = lazy(() => import("../pages/VerifyEmail"));
const ForgotPasswordPage = lazy(() => import("../pages/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("../pages/ResetPassword"));
// Add near the other lazy pages at the top:
const OnboardingPage = lazy(() => import("../pages/Onboarding"));

/* Legal pages */
const PrivacyPolicyPage = lazy(() => import("../pages/legal/PrivacyPolicy"));
const TermsOfServicePage = lazy(() => import("../pages/legal/TermsOfService"));
const CookieDisclosurePage = lazy(() => import("../pages/legal/CookieDisclosure"));
const SmsOptInEvidencePage = lazy(() => import("../pages/legal/SmsOptInEvidence"));
const ContactPage = lazy(() => import("../pages/Contact"));

const DashboardPage = lazy(() => import("../pages/Dashboard"));
const DiscoverPage = lazy(() => import("../pages/Discover"));
const PostPage = lazy(() => import("../pages/Post"));
const PostDetailPage = lazy(() => import("../pages/PostDetail"));
const InboxPage = lazy(() => import("../pages/Inbox"));
const ConversationPage = lazy(() => import("../pages/Conversation"));
const ProfilePage = lazy(() => import("../pages/Profile"));
const ProfileEditPage = lazy(() => import("../pages/ProfileEdit"));
const BookmarksPage = lazy(() => import("../pages/Bookmarks"));
const RequestsPage = lazy(() => import("../pages/Requests"));
const SettingsPage = lazy(() => import("../pages/Settings"));
const NotificationsPage = lazy(() => import("../pages/Notifications"));
const PricingPage = lazy(() => import("../pages/Pricing"));
const FeedbackRequestHubPage = lazy(() => import("../pages/feedbackRequest/Hub"));
const FeedbackRequestNewPage = lazy(() => import("../pages/feedbackRequest/Submit"));
const FeedbackRequestDetailPage = lazy(() => import("../pages/feedbackRequest/Detail"));
const FeedbackRequestAdminPage = lazy(() => import("../pages/feedbackRequest/AdminQueue"));
const FeedbackRequestAdminReadersPage = lazy(() => import("../pages/feedbackRequest/AdminReaders"));
const ScriptReaderPage = lazy(() => import("../pages/feedbackRequest/ScriptReader"));
const FinishFeedbackPage = lazy(() => import("../pages/feedbackRequest/FinishFeedback"));

const ProfileSetupPage = lazy(() => import("../pages/ProfileSetup"));
const NotFoundPage = lazy(() => import("../pages/NotFound"));
const SkeletonTestPage = lazy(() => import("../pages/SkeletonTest"));
const WaitlistPage = lazy(() => import("../pages/Waitlist"));
const AdminPanelPage = lazy(() => import("../pages/AdminPanel"));

function Protected({ children }) {
  const { user, isInitialized } = useAuth();
  const location = useLocation();

  // Wait for auth state to be initialized before making redirect decisions
  if (!isInitialized) {
    return null;
  }

  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // User hasn't completed onboarding - redirect to onboarding (except if already there)
  if (user.onboardingComplete === false && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // User is authenticated and has completed onboarding (or is in onboarding flow)
  return children;
}

const Fallback = () => (
  <div className="p-6 text-neutral-400">Loading…</div>
);

export default function App() {
  // Sync theme with backend on login
  useThemeSync();
  
  const location = useLocation();
  
  // Initialize analytics on mount
  useEffect(() => {
    initAnalytics();
  }, []);
  
  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  
  return (
    <>
      <ConsentBanner />
      <Suspense fallback={<Fallback />}>
      <Routes>
        {/* Marketing */}
        <Route element={<MarketingLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="about-us" element={<Navigate to="/#about" replace />} />
          <Route path="about" element={<Navigate to="/#about" replace />} />
          <Route path="features" element={<Navigate to="/#features" replace />} />
          <Route path="join" element={<JoinPage />} />
          <Route path="signup" element={<JoinPage />} />
          <Route path="login" element={<LoginPage />} />
          {/* New auth form skeleton pages */}
          <Route path="signup-page" element={<SignupPage />} />
          <Route path="login-page" element={<LoginPageSkeleton />} />
          <Route path="skeleton-test" element={<SkeletonTestPage />} />
          {/* Legacy /setup redirects to /onboarding */}
          <Route path="setup" element={<Navigate to="/onboarding" replace />} />
          {/* Legal pages */}
          <Route path="legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="legal/terms" element={<TermsOfServicePage />} />
          <Route path="legal/cookies" element={<CookieDisclosurePage />} />
          <Route path="sms-opt-in-evidence" element={<SmsOptInEvidencePage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="waitlist" element={<WaitlistPage />} />
        </Route>

        {/* Auth flows - standalone pages without layout */}
        <Route path="verify-email" element={<VerifyEmailPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />

        {/* Full-screen viewers — no AppLayout chrome */}
        <Route
          path="feedback-request/:id/read"
          element={
            <Protected>
              <ScriptReaderPage />
            </Protected>
          }
        />
        <Route
          path="feedback-request/:id/finish"
          element={
            <Protected>
              <FinishFeedbackPage />
            </Protected>
          }
        />

        {/* Authenticated app shell */}
        <Route element={<AppLayout />}>

          {/* Dashboard */}
          <Route
            path="dashboard"
            element={
              <Protected>
                <DashboardPage />
              </Protected>
            }
          />

          {/* Legacy mappings */}
          <Route
            path="feed"
            element={
              <Protected>
                <DashboardPage />
              </Protected>
            }
          />
          <Route
            path="search"
            element={
              <Protected>
                <DiscoverPage />
              </Protected>
            }
          />

          {/* New pages */}
          <Route
            path="discover"
            element={
              <Protected>
                <DiscoverPage />
              </Protected>
            }
          />
          <Route
            path="post"
            element={
              <Protected>
                <PostPage />
              </Protected>
            }
          />
          <Route
            path="posts/:id"
            element={
              <Protected>
                <PostDetailPage />
              </Protected>
            }
          />
          <Route
            path="inbox"
            element={
              <Protected>
                <InboxPage />
              </Protected>
            }
          />
          <Route
            path="inbox/:id"
            element={
              <Protected>
                <ConversationPage />
              </Protected>
            }
          />
          <Route
            path="profile/:id?"
            element={
              <Protected>
                <ProfilePage />
              </Protected>
            }
          />
          <Route
            path="profile-edit"
            element={
              <Protected>
                <ProfileEditPage />
              </Protected>
            }
          />
          <Route
            path="bookmarks"
            element={
              <Protected>
                <BookmarksPage />
              </Protected>
            }
          />
          <Route
            path="requests"
            element={
              <Protected>
                <RequestsPage />
              </Protected>
            }
          />
          <Route
            path="admin"
            element={
              <Protected>
                <AdminPanelPage />
              </Protected>
            }
          />
          <Route
            path="settings"
            element={
              <Protected>
                <SettingsPage />
              </Protected>
            }
          />
          <Route
            path="notifications"
            element={
              <Protected>
                <NotificationsPage />
              </Protected>
            }
          />
          <Route
            path="pricing"
            element={
              <Protected>
                <PricingPage />
              </Protected>
            }
          />
          <Route
            path="subscribe"
            element={
              <Protected>
                <PricingPage />
              </Protected>
            }
          />

          {/* Script Feedback Service (paid marketplace, PDF Part 2) */}
          <Route
            path="feedback-request"
            element={
              <Protected>
                <FeedbackRequestHubPage />
              </Protected>
            }
          />
          <Route
            path="feedback-request/new"
            element={
              <Protected>
                <FeedbackRequestNewPage />
              </Protected>
            }
          />
          <Route
            path="feedback-request/admin"
            element={
              <Protected>
                <FeedbackRequestAdminPage />
              </Protected>
            }
          />
          <Route
            path="feedback-request/admin/readers"
            element={
              <Protected>
                <FeedbackRequestAdminReadersPage />
              </Protected>
            }
          />
          <Route
            path="feedback-request/:id"
            element={
              <Protected>
                <FeedbackRequestDetailPage />
              </Protected>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Global 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </>
  );
}
